import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  Divider, Card, CardContent, CircularProgress, 
  Chip, List, ListItem, ListItemText, Accordion, 
  AccordionSummary, AccordionDetails, Alert, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Slider,
  FormControlLabel, Switch, Tooltip, IconButton,
  ListItemIcon, LinearProgress
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon,
  Psychology as PsychologyIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Storage as StorageIcon,
  Upload as UploadIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  TableChart as CsvIcon
} from '@mui/icons-material';
import { generateEmailResponse, classifyEmail, AIEmailResponse } from '../services/emailAIService';
import { EmailMessage } from '../services/emailService';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

// Interface para as propriedades do componente
interface EmailAIContentProps {
  onSendEmail?: (email: EmailMessage) => void;
}

// Componente principal
const EmailAIContent: React.FC<EmailAIContentProps> = ({ onSendEmail }) => {
  // Estados
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [recipient, setRecipient] = useState('');
  const [generatedResponse, setGeneratedResponse] = useState<AIEmailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailCategory, setEmailCategory] = useState<{ category: string; confidence: number } | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [useRAG, setUseRAG] = useState(true);
  const [knowledgeFiles, setKnowledgeFiles] = useState<{id: string; name: string; type: string; size: number; status: string; progress: number}[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [editingResponse, setEditingResponse] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Obter leads do Redux store
  const leads = useSelector((state: RootState) => state.leads.items);
  
  // Efeito para classificar o e-mail quando o assunto ou corpo mudar
  useEffect(() => {
    const classifyEmailContent = async () => {
      if (emailSubject.trim() && emailBody.trim()) {
        try {
          const mockEmail: EmailMessage = {
            id: 'temp-id',
            from: recipient,
            to: 'contato@imobiliaria.com',
            subject: emailSubject,
            body: emailBody,
            date: new Date(),
            status: 'unread',
            read: false,
            replied: false
          };
          
          const classification = await classifyEmail(mockEmail);
          setEmailCategory(classification);
        } catch (error) {
          console.error('Erro ao classificar e-mail:', error);
        }
      } else {
        setEmailCategory(null);
      }
    };
    
    // Debounce para não fazer muitas chamadas
    const timer = setTimeout(classifyEmailContent, 1000);
    return () => clearTimeout(timer);
  }, [emailSubject, emailBody, recipient]);
  
  // Função para gerar resposta
  const handleGenerateResponse = async () => {
    if (!emailSubject.trim() || !emailBody.trim() || !recipient.trim()) {
      setSnackbarMessage('Por favor, preencha todos os campos.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    setLoading(true);
    try {
      const mockEmail: EmailMessage = {
        id: 'temp-id',
        from: recipient,
        to: 'contato@imobiliaria.com',
        subject: emailSubject,
        body: emailBody,
        date: new Date(),
        status: 'unread',
        read: false,
        replied: false
      };
      
      const response = await generateEmailResponse(mockEmail, null, {
        temperature,
        model: 'gpt-4',
        personality: 'profissional e prestativo'
      });
      
      setGeneratedResponse(response);
      setEditedResponse(response.body);
    } catch (error) {
      console.error('Erro ao gerar resposta:', error);
      setSnackbarMessage('Erro ao gerar resposta. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para enviar e-mail
  const handleSendEmail = () => {
    if (!generatedResponse) return;
    
    const emailToSend: EmailMessage = {
      id: `email-${Date.now()}`,
      from: 'contato@imobiliaria.com',
      to: recipient,
      subject: generatedResponse.subject,
      body: editingResponse ? editedResponse : generatedResponse.body,
      date: new Date(),
      status: 'sent',
      read: true,
      replied: false
    };
    
    if (onSendEmail) {
      onSendEmail(emailToSend);
    }
    
    setSnackbarMessage('E-mail enviado com sucesso!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    
    // Limpar formulário
    setEmailSubject('');
    setEmailBody('');
    setRecipient('');
    setGeneratedResponse(null);
    setEditingResponse(false);
    setEditedResponse('');
  };
  
  // Função para copiar resposta para a área de transferência
  const handleCopyResponse = () => {
    if (!generatedResponse) return;
    
    navigator.clipboard.writeText(editingResponse ? editedResponse : generatedResponse.body);
    setSnackbarMessage('Resposta copiada para a área de transferência!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };
  
  // Função para alternar modo de edição
  const toggleEditMode = () => {
    if (!editingResponse && generatedResponse) {
      setEditedResponse(generatedResponse.body);
    }
    setEditingResponse(!editingResponse);
  };
  
  // Função para lidar com o upload de arquivos
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const fileType = file.name.split('.').pop()?.toLowerCase() || '';
    
    if (!['pdf', 'txt', 'csv'].includes(fileType)) {
      setSnackbarMessage('Formato de arquivo não suportado. Use PDF, TXT ou CSV.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    setUploadingFile(true);
    
    // Criar um ID único para o arquivo
    const fileId = `file-${Date.now()}`;
    
    // Adicionar o arquivo à lista com status de processamento
    const newFile = {
      id: fileId,
      name: file.name,
      type: fileType,
      size: file.size,
      status: 'processing',
      progress: 0
    };
    
    setKnowledgeFiles(prev => [...prev, newFile]);
    
    try {
      // Simular o processamento do arquivo com progresso
      const totalSteps = 10;
      for (let step = 1; step <= totalSteps; step++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Atualizar o progresso
        setKnowledgeFiles(prev => 
          prev.map(f => 
            f.id === fileId 
              ? { ...f, progress: Math.round((step / totalSteps) * 100) } 
              : f
          )
        );
      }
      
      // Atualizar o status para pronto
      setKnowledgeFiles(prev => 
        prev.map(f => 
          f.id === fileId 
            ? { ...f, status: 'ready', progress: 100 } 
            : f
        )
      );
      
      setSnackbarMessage(`Arquivo "${file.name}" processado com sucesso e adicionado à base de conhecimento.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Em um ambiente real, aqui enviaríamos o arquivo para processamento no servidor
      // const formData = new FormData();
      // formData.append('file', file);
      // const response = await fetch('/api/knowledge/upload', {
      //   method: 'POST',
      //   body: formData
      // });
      // const data = await response.json();
      // if (data.success) {
      //   setKnowledgeFiles(prev => [...prev, { ...data.file, status: 'ready' }]);
      // } else {
      //   throw new Error(data.message);
      // }
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      
      // Atualizar o status para erro
      setKnowledgeFiles(prev => 
        prev.map(f => 
          f.id === fileId 
            ? { ...f, status: 'error' } 
            : f
        )
      );
      
      setSnackbarMessage(`Erro ao processar o arquivo "${file.name}". Tente novamente.`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploadingFile(false);
      
      // Limpar o input de arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Função para excluir um arquivo da base de conhecimento
  const handleDeleteFile = (fileId: string) => {
    setKnowledgeFiles(prev => prev.filter(f => f.id !== fileId));
    
    setSnackbarMessage('Arquivo removido da base de conhecimento.');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
    
    // Em um ambiente real, aqui enviaríamos uma requisição para excluir o arquivo no servidor
    // fetch(`/api/knowledge/delete/${fileId}`, {
    //   method: 'DELETE'
    // });
  };
  
  // Renderizar componente
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Assistente de E-mail com IA
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Use a inteligência artificial para gerar respostas profissionais para e-mails, com base no conteúdo do e-mail e na base de conhecimento.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              E-mail Recebido
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="recipient-label">De (Remetente)</InputLabel>
              <Select
                labelId="recipient-label"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                label="De (Remetente)"
              >
                <MenuItem value="">
                  <em>Selecione um remetente</em>
                </MenuItem>
                {leads.map((lead) => (
                  <MenuItem key={lead.id} value={lead.email}>
                    {lead.name} ({lead.email})
                  </MenuItem>
                ))}
                <MenuItem value="cliente@exemplo.com">Novo Cliente (cliente@exemplo.com)</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Assunto"
              variant="outlined"
              margin="normal"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
            
            <TextField
              fullWidth
              label="Conteúdo do E-mail"
              variant="outlined"
              margin="normal"
              multiline
              rows={10}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
            />
            
            {emailCategory && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Classificação do E-mail:
                </Typography>
                <Chip 
                  label={`${emailCategory.category} (${Math.round(emailCategory.confidence * 100)}% de confiança)`}
                  color="primary"
                  variant="outlined"
                  icon={<InfoIcon />}
                />
              </Box>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<ExpandMoreIcon />}
                  onClick={() => setShowSettings(!showSettings)}
                  sx={{ mr: 1 }}
                >
                  {showSettings ? 'Ocultar Configurações' : 'Mostrar Configurações'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<StorageIcon />}
                  onClick={() => setShowKnowledgeBase(!showKnowledgeBase)}
                >
                  {showKnowledgeBase ? 'Ocultar Base de Conhecimento' : 'Base de Conhecimento'}
                </Button>
              </Box>
              
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={handleGenerateResponse}
                disabled={loading || !emailSubject || !emailBody || !recipient}
              >
                Gerar Resposta
              </Button>
            </Box>
            
            {showSettings && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Configurações de IA
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useRAG}
                        onChange={(e) => setUseRAG(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Usar base de conhecimento (RAG)"
                  />
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography id="temperature-slider" gutterBottom>
                      Criatividade: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, newValue) => setTemperature(newValue as number)}
                      aria-labelledby="temperature-slider"
                      step={0.1}
                      marks
                      min={0}
                      max={1}
                      valueLabelDisplay="auto"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Valores mais baixos geram respostas mais previsíveis, valores mais altos geram respostas mais criativas.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {showKnowledgeBase && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <StorageIcon sx={{ mr: 1 }} />
                    Base de Conhecimento
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Carregue arquivos PDF, TXT ou CSV para enriquecer a base de conhecimento do assistente de e-mail.
                    Esses arquivos serão processados e convertidos em embeddings para melhorar as respostas.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                      sx={{ mr: 1 }}
                    >
                      Carregar Arquivo
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept=".pdf,.txt,.csv"
                      onChange={handleFileUpload}
                    />
                    
                    <Typography variant="caption" color="text.secondary">
                      Formatos suportados: PDF, TXT, CSV
                    </Typography>
                  </Box>
                  
                  {knowledgeFiles.length > 0 ? (
                    <List dense>
                      {knowledgeFiles.map((file) => (
                        <ListItem
                          key={file.id}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleDeleteFile(file.id)}>
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            {file.type === 'pdf' ? <PdfIcon color="error" /> : 
                             file.type === 'csv' ? <CsvIcon color="primary" /> : 
                             <FileIcon color="action" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={file.name}
                            secondary={
                              <>
                                {file.status === 'processing' ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={file.progress} 
                                      sx={{ width: '100%', mr: 1 }} 
                                    />
                                    <Typography variant="caption">
                                      {file.progress}%
                                    </Typography>
                                  </Box>
                                ) : (
                                  `${file.type.toUpperCase()} - ${(file.size / 1024).toFixed(1)} KB - ${file.status}`
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum arquivo carregado na base de conhecimento.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Resposta Gerada
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Gerando resposta...
                </Typography>
              </Box>
            ) : generatedResponse ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">
                    Assunto: {generatedResponse.subject}
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {editingResponse ? (
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={12}
                    value={editedResponse}
                    onChange={(e) => setEditedResponse(e.target.value)}
                  />
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                    {generatedResponse.body}
                  </Typography>
                )}
                
                {generatedResponse.suggestedActions && generatedResponse.suggestedActions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Ações Sugeridas:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {generatedResponse.suggestedActions.map((action, index) => (
                        <Chip key={index} label={action} variant="outlined" size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {generatedResponse.referencedSources && generatedResponse.referencedSources.length > 0 && (
                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Fontes de Conhecimento Utilizadas</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {generatedResponse.referencedSources.map((source, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`Fonte ${index + 1}: ${source.metadata.source}`}
                              secondary={source.content.length > 100 ? `${source.content.substring(0, 100)}...` : source.content}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Tooltip title={editingResponse ? "Salvar edições" : "Editar resposta"}>
                      <IconButton onClick={toggleEditMode} color={editingResponse ? "primary" : "default"}>
                        {editingResponse ? <SaveIcon /> : <EditIcon />}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Copiar resposta">
                      <IconButton onClick={handleCopyResponse}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Gerar nova resposta">
                      <IconButton onClick={handleGenerateResponse} disabled={loading}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={handleSendEmail}
                  >
                    Enviar E-mail
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column' }}>
                <PsychologyIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" align="center">
                  Preencha os detalhes do e-mail recebido e clique em "Gerar Resposta" para criar uma resposta profissional usando IA.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailAIContent;
